import { observer } from 'mobx-react-lite'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Draggable } from 'react-beautiful-dnd';
import { CameraVideo, CardImage, EyeFill, FileEarmark, FileEarmarkMusic, FileEarmarkPdf, FileEarmarkText, Share, ThreeDotsVertical, Trash } from 'react-bootstrap-icons'
import { CampaignContext } from '../../../../../../contexts/CampaignContext';
import { toastSuccess } from '../../../../../FormComponents/Toasts';
import { useReadOnly } from '../../../../../../contexts/ReadOnlyContext';

const IMAGE_EXTENSIONS = ['.jpeg', '.jpg', '.png', '.svg', '.heic']
const VIDEO_EXTENSIONS = ['.avi', '.mp4', '.mov']
const AUDIO_EXTENSIONS = ['.mp3', '.flac', '.wav']
const PDF_EXTENSIONS = ['.pdf']

const hasExtension = (fileName, extensions) => {
  const normalizedFileName = fileName.toLowerCase()

  return extensions.some(extension => normalizedFileName.endsWith(extension))
}

function PreviewFallback({ icon: Icon, label, description, onClick }) {
  return (
    <button
      type="button"
      className="h-full w-full rounded-b-md bg-gray-100 dark:bg-gray-800 flex flex-col items-center justify-center gap-2 text-center cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
      onClick={onClick}
      aria-label={`Open ${label}`}
    >
      <div className="h-16 w-16 rounded-full bg-white dark:bg-rb-dark-mode-lighter flex items-center justify-center shadow-sm">
        <Icon className="w-8 h-8 text-gray-400 dark:text-gray-300" />
      </div>
      <div>
        <p className="text-sm font-semibold text-gray-600 dark:text-gray-200">{label}</p>
        {description && <p className="text-xs text-gray-400 dark:text-gray-500">{description}</p>}
      </div>
    </button>
  )
}

export default observer(function FileItem({ document, deleteDocument, index }) {
  const { campaign } = useContext(CampaignContext)
  const readOnly = useReadOnly()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [pdfThumbnailFailed, setPdfThumbnailFailed] = useState(false)

  const documentPath = `/api/project_manager/campaigns/${campaign.id}/documents/${document.id}`
  const openDocument = () => window.open(documentPath)

  const fileIsImage = useMemo(() => hasExtension(document.file_name, IMAGE_EXTENSIONS), [document.file_name])
  const fileIsVideo = useMemo(() => hasExtension(document.file_name, VIDEO_EXTENSIONS), [document.file_name])
  const fileIsAudio = useMemo(() => hasExtension(document.file_name, AUDIO_EXTENSIONS), [document.file_name])
  const fileIsPdf = useMemo(() => hasExtension(document.file_name, PDF_EXTENSIONS), [document.file_name])

  useEffect(() => {
    setPdfThumbnailFailed(false)
  }, [document.thumb_url])

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen)

  const handlePdfThumbnailLoad = event => {
    const { naturalHeight, naturalWidth } = event.currentTarget

    if (!naturalHeight || !naturalWidth) {
      setPdfThumbnailFailed(true)
    }
  }

  const copySharingUrlToClipboard = () => {
    const sharingLink = `${window.location.origin}/api/documents/${document.token}`
    navigator.clipboard.writeText(sharingLink);
    toastSuccess('Link copied to clipboard')
  }

  const icon = useMemo(() => {
    if(fileIsImage) return <CardImage />
    if(fileIsVideo) return <CameraVideo />
    if(fileIsAudio) return <FileEarmarkMusic />
    if(fileIsPdf) return <FileEarmarkPdf />
    return <FileEarmarkText />
  }, [fileIsImage, fileIsVideo, fileIsAudio, fileIsPdf])

  const getExtension = document => document.file_name.match(/\.([0-9a-z]+)$/i)?.[1] || ''

  return (
    <Draggable key={document.id} draggableId={`${document.id}`} index={index} isDragDisabled={readOnly}>
      {(provided) => (
        <div
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
        >
          <div className="flex flex-col h-80 w-64 bg-white dark:bg-rb-dark-mode border-1 border-gray-200 dark:border-rb-dark-mode-lighter rounded-md overflow-hidden">
            <div className="flex items-center justify-between font-bold pl-4 pr-0 py-2 shrink-0">
              <div className="flex items-center">
                <div className="dark:text-white">{icon}</div>
                <p className="ml-2 dark:text-white">{document.file_name.length > 15 ? `${document.file_name.slice(0, 15)}...` : document.file_name}</p>
              </div>
              <div className="relative">
                <button className="p-2 rounded-md dark:text-white" onClick={toggleDropdown}><ThreeDotsVertical /></button>
                {dropdownOpen && <div className="absolute bg-green dark:bg-rb-dark-mode-lighter h-full rounded-md flex" style={{ left: readOnly ? 0 : -45 }}>
                  {!readOnly && <button className="bg-gray-400 dark:bg-rb-dark-mode-lighter text-white dark:text-white py-2 px-3 rounded-l-md z-10 hover:bg-rb-green dark:hover:bg-rb-green" onClick={deleteDocument}><Trash /></button>}
                  <button className={`bg-gray-400 dark:bg-rb-dark-mode-lighter text-white dark:text-white z-10 py-2 px-3 hover:bg-rb-green dark:hover:bg-rb-green ${readOnly ? 'rounded-l-md' : ''}`} onClick={copySharingUrlToClipboard}><Share /></button>
                  <a className="bg-gray-400 dark:bg-rb-dark-mode-lighter text-white dark:text-white z-10 py-2 px-3 rounded-r-md hover:bg-rb-green dark:hover:bg-rb-green" href={`/api/project_manager/campaigns/${campaign.id}/documents/${document.id}`} target="_blank" rel="noopener noreferrer"><EyeFill/></a>
                </div>}
              </div>
            </div>
            <div className="flex-1 min-h-0 w-full rounded-b-md bg-gray-100 dark:bg-gray-800 overflow-hidden">
              {!!fileIsImage && <img className="h-full w-full object-contain" src={document.url} alt="" onClick={openDocument} />}
              {!!fileIsVideo && <video className="h-full w-full object-contain" controls={false} onClick={openDocument}>
                  <source src={document.url} type={`video/${getExtension(document)}`} />
                  Your browser does not support the video tag.
                </video>
              }
              {!!fileIsAudio && <audio className="h-full w-full px-4" controls onClick={openDocument}>
                  <source src={document.url} type={`audio/${getExtension(document)}`} />
                  Your browser does not support the audio tag.
                </audio>
              }
              {!!fileIsPdf && (document.thumb_url && !pdfThumbnailFailed
                ? <img className="h-full w-full object-contain" src={document.thumb_url} alt="" onClick={openDocument} onLoad={handlePdfThumbnailLoad} onError={() => setPdfThumbnailFailed(true)} />
                : <PreviewFallback
                    icon={FileEarmarkPdf}
                    label="PDF document"
                    description="Preview unavailable"
                    onClick={openDocument}
                  />
              )}
              {!fileIsAudio && !fileIsImage && !fileIsPdf && !fileIsVideo && <PreviewFallback
                  icon={FileEarmark}
                  label="Document"
                  description="Open file"
                  onClick={openDocument}
                />
              }
            </div>
          </div>
        </div>
      )}
    </Draggable>
  )
})